/**
 * ProfessionalSection — Professional credentials and practice details.
 *
 * Allows practicing CA/CS professionals to maintain their professional
 * credentials and access their practice dashboard.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/constants';
import { EditableFieldsCard, ProfileCard, Pill, FieldRow } from './ProfilePrimitives';
import Button from '../common/Button';
import { professionalAssignmentAPI } from '../../services/api';

const MUTED = '#7a7060';

const PROFESSION_OPTIONS = [
  { value: 'CA', label: 'Chartered Accountant' },
  { value: 'CS', label: 'Company Secretary' },
  { value: 'CMA', label: 'Cost Accountant' },
  { value: 'Advocate', label: 'Advocate' },
  { value: 'Other', label: 'Other Professional' },
];

export default function ProfessionalSection({ user }) {
  const { updateProfile } = useAuth();
  const navigate = useNavigate();
  const [clientCount, setClientCount] = useState(null);
  const [loadingClients, setLoadingClients] = useState(true);

  // Fetch client count for practice dashboard
  useEffect(() => {
    const fetchClientCount = async () => {
      try {
        const response = await professionalAssignmentAPI.getAssignments();
        if (response.success) {
          const activeAssignments = response.data?.assignments?.filter(
            assignment => assignment.status === 'active'
          ) || [];
          setClientCount(activeAssignments.length);
        }
      } catch (error) {
        console.error('Failed to fetch client count:', error);
      } finally {
        setLoadingClients(false);
      }
    };

    if (user?.roleName === 'professional') {
      fetchClientCount();
    }
  }, [user]);

  const professionalFields = [
    {
      name: 'profession',
      label: 'Profession',
      type: 'select',
      options: PROFESSION_OPTIONS,
      required: true,
    },
    {
      name: 'membershipNo',
      label: 'Membership Number',
      placeholder: 'e.g., FCA-123456',
      required: true,
    },
    {
      name: 'copNumber',
      label: 'Certificate of Practice Number',
      placeholder: 'e.g., 12345 or FCA/DEL/123456',
    },
    {
      name: 'isPractising',
      label: 'Currently Practising',
      type: 'checkbox',
      helper: 'Required for UDIN generation',
    },
    {
      name: 'firmName',
      label: 'Firm Name',
      placeholder: 'Your firm name',
    },
    {
      name: 'firmRegistrationNumber',
      label: 'Firm Registration Number',
      placeholder: 'Firm registration number',
    },
    {
      name: 'gstIn',
      label: 'GSTIN',
      placeholder: 'e.g., 07DEPCS7390F1Z1',
    },
  ];

  const handleSaveProfessional = async (updates) => {
    try {
      await updateProfile(updates);
      return { success: true };
    } catch (error) {
      console.error('Failed to update professional details:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to update professional details' 
      };
    }
  };

  const goToPracticeDashboard = () => {
    navigate(ROUTES.PROFESSIONAL_DASHBOARD);
  };

  return (
    <div className="space-y-6">
      {/* Professional Details Card */}
      <EditableFieldsCard
        title="Professional Details"
        subtitle="Membership and practice credentials"
        icon="🎓"
        fields={professionalFields}
        values={{
          profession: user?.profession || '',
          membershipNo: user?.membershipNo || '',
          copNumber: user?.copNumber || '',
          isPractising: user?.isPractising || false,
          firmName: user?.firmName || '',
          firmRegistrationNumber: user?.firmRegistrationNumber || '',
          gstIn: user?.gstIn || '',
        }}
        onSave={handleSaveProfessional}
      />

      {/* My Practice Card */}
      <ProfileCard
        title="My Practice"
        subtitle="Client assignments and practice management"
        icon="💼"
        action={
          <Button variant="outline" size="sm" onClick={goToPracticeDashboard}>
            Open Practice Dashboard
          </Button>
        }
      >
        <div className="space-y-4">
          {/* Client Count */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[12.5px]" style={{ color: MUTED }}>
                Active Client Companies
              </div>
              <div className="text-[18px] font-semibold" style={{ color: '#08111f' }}>
                {loadingClients ? '...' : clientCount || 0}
              </div>
            </div>
            <div className="text-right">
              {loadingClients ? (
                <div className="text-[11px]" style={{ color: MUTED }}>
                  Loading...
                </div>
              ) : clientCount > 0 ? (
                <Pill tone="green">
                  Active Practice
                </Pill>
              ) : (
                <Pill tone="neutral">
                  No Active Clients
                </Pill>
              )}
            </div>
          </div>

          {/* Practice Status */}
          <div className="pt-2 border-t" style={{ borderColor: '#ddd6c6' }}>
            <div className="grid grid-cols-2 gap-4 text-[11.5px]">
              <div>
                <div style={{ color: MUTED }}>Practising Status</div>
                <div className="font-medium" style={{ color: '#08111f' }}>
                  {user?.isPractising ? (
                    <Pill tone="green">Practising</Pill>
                  ) : (
                    <Pill tone="neutral">Not Practising</Pill>
                  )}
                </div>
              </div>
              <div>
                <div style={{ color: MUTED }}>Professional Type</div>
                <div className="font-medium" style={{ color: '#08111f' }}>
                  {PROFESSION_OPTIONS.find(opt => opt.value === user?.profession)?.label || 'Not Set'}
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2 pt-2">
            <Button variant="primary" size="sm" onClick={goToPracticeDashboard}>
              View All Assignments
            </Button>
            {clientCount > 0 && (
              <Button variant="outline" size="sm" onClick={goToPracticeDashboard}>
                Manage Access Requests
              </Button>
            )}
          </div>
        </div>
      </ProfileCard>
    </div>
  );
}
